import React, { ReactNode } from 'react';
import { makeStyles } from '@fluentui/react-components';
import { AlyzeOptimizedImage } from '../../../utilities/AlyzeOptimizedImage';
import { AlyzeVideo } from '../../../utilities/AlyzeVideo';

const useStyles = makeStyles({
    heroSection: {
        alignSelf: 'stretch',
        height: 'auto',
        minHeight: '880px',
        padding: '80px',
        backgroundColor: '#222222',
        justifyContent: 'flex-start',
        alignItems: 'flex-end',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        '@media (max-width: 1200px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '40px',
            minHeight: '700px',
        },
        '@media (max-width: 768px)': {
            minHeight: '600px',
            padding: '40px 16px',
            gap: '32px',
        },
    },

    heroBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        '& img, & video': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(193deg, rgba(0, 0, 0, 0) 0%, black 100%)',
        },
    },

    heroContent: {
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: '32px',
        display: 'inline-flex',
        position: 'relative',
        zIndex: 2,
        '@media (max-width: 1200px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '40px',
        },
        '@media (max-width: 768px)': {
            gap: '24px',
        },
    },

    decorativeA: {
        width: '880px',
        height: '880px',
        position: 'absolute',
        right: '-230px',
        top: '150px',
        zIndex: 1,
        '@media (max-width: 1200px)': {
            width: '600px',
            height: '600px',
            top: '200px',
            right: '-200px',
        },
        '@media (max-width: 768px)': {
            display: 'none',
        },
    },
});

export interface AlyzeHeroSectionProps {
    /** Background image source URL */
    backgroundImage?: string;
    /** Background video source URL */
    backgroundVideo?: string;
    /** Alt text for background image (if using image) */
    backgroundAlt?: string;
    /** Whether to show the decorative A SVG */
    showDecorativeA?: boolean;
    /** Children components (typically text and CTA buttons) */
    children: ReactNode;
    /** Optional custom className for the hero section */
    className?: string;
}

export const AlyzeHeroSection: React.FC<AlyzeHeroSectionProps> = ({
    backgroundImage,
    backgroundVideo,
    backgroundAlt = 'Hero background',
    showDecorativeA = false,
    children,
    className,
}) => {
    const styles = useStyles();

    return (
        <div className={`${styles.heroSection} ${className || ''}`}>
            <div className={styles.heroBackground}>
                {backgroundVideo ? (
                    <AlyzeVideo 
                        src={backgroundVideo}
                        autoPlay 
                        loop 
                        muted 
                        playsInline
                        controls={false}
                    />
                ) : backgroundImage ? (
                    <AlyzeOptimizedImage
                        src={backgroundImage}
                        alt={backgroundAlt}
                        width={1729}
                        height={748}
                    />
                ) : null}
            </div>

            {showDecorativeA && (
                <svg className={styles.decorativeA} viewBox="0 0 880 880" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M424.833 188.864L186.36 836.025C177.649 862.279 154.004 879.836 127.714 879.836H0L295.096 44.4676C304.43 17.7214 328.542 0 355.453 0H525.791C552.858 0 576.97 17.8855 586.148 44.6317L880 880H752.441C725.996 880 702.507 862.443 693.795 836.189L455.167 188.864C450.189 173.604 429.811 173.604 424.833 188.864Z" fill="#F2F2F2" fillOpacity="0.35"/>
                </svg>
            )}

            <div className={styles.heroContent}>
                {children}
            </div>
        </div>
    );
};
